import { useEffect, useState } from "react";
import { BadgeCheck, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppLayout";
import { PricingCards } from "@/components/PricingCards";
import { REVIEW_PRICE, REVIEW_MEMBER_PRICE } from "@/lib/constants";

const NAMES = { proposal_writer_monthly: "Proposal Writer", licensing_guide_monthly: "State Licensing Guide", verify_suite_monthly: "Full Verify Suite",
  proposal_writer_yearly: "Proposal Writer (yearly)", licensing_guide_yearly: "State Licensing Guide (yearly)", verify_suite_yearly: "Full Verify Suite (yearly)" };

function RefundCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing/refund").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st || st.reason === "no_plan") return null;
  const until = st.deadline ? new Date(st.deadline).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";
  const go = async () => {
    if (!window.confirm(`Refund your first plan payment ($${st.amount}) and end ${NAMES[st.lookup_key] || "your plan"} now? This can't be undone.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/billing/refund"); toast.success(`Refund of $${data.amount} started. It usually reaches your card in 5–10 business days.`); setTimeout(() => window.location.reload(), 1500); }
    catch (e) { toast.error(formatErr(e)); setBusy(false); }
  };
  return (
    <div className="mb-6 bg-white border border-slate-200 border-l-4 border-l-emerald-500 rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid="refund-card">
      <ShieldCheck className="h-6 w-6 text-emerald-600 shrink-0" />
      <div className="flex-1 min-w-[14rem] text-sm">
        <div className="font-semibold text-slate-900">14-day money-back guarantee</div>
        {st.eligible ? <p className="text-slate-600" data-testid="refund-eligible">You can refund your first plan payment in full until {until}. After that, all sales are final.</p>
          : <p className="text-slate-500" data-testid="refund-ineligible">{st.reason === "already_refunded" ? "Your first plan payment was refunded." : `Your refund window ended on ${until}. After 14 days all sales are final.`}</p>}
      </div>
      {st.eligible && <Button variant="outline" onClick={go} disabled={busy} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="refund-button">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Refund my first payment</Button>}
    </div>
  );
}

function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const load = () => api.get("/billing/subscriptions").then((r) => setSubs(r.data));
  useEffect(() => { load(); }, []);
  if (!subs.length) return null;
  const toggle = async (s) => {
    if (!s.cancel_at_period_end && !window.confirm("Cancel this subscription? You keep access until the end of the period you paid for.")) return;
    try { await api.post(`/billing/subscriptions/${s.subscription_id}/cancel`, { cancel: !s.cancel_at_period_end }); toast.success(s.cancel_at_period_end ? "Subscription resumed" : "Cancellation scheduled"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="mb-10 bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="my-subscriptions">
      {subs.map((s) => (
        <div key={s.subscription_id} className="p-5 flex flex-wrap items-center gap-3">
          <div className="flex-1"><div className="font-semibold text-slate-900">{NAMES[s.lookup_key] || s.lookup_key}</div>
            <div className="text-xs text-slate-500">{s.status === "active" ? (s.cancel_at_period_end ? `Cancels${s.current_period_end ? " on " + new Date(s.current_period_end * 1000).toLocaleDateString() : " at period end"}` : "Active · renews " + (s.lookup_key?.endsWith("_yearly") ? "yearly" : "monthly")) : s.status === "past_due" ? "Payment failed — access paused until paid" : "Cancelled"}</div></div>
          {s.status !== "canceled" && <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`sub-toggle-${s.subscription_id}`}>{s.cancel_at_period_end ? "Resume" : "Cancel"}</Button>}
        </div>
      ))}
    </div>
  );
}

export default function Billing() {
  return (
    <div>
      <PageHeader eyebrow="Plans & billing" title="Choose your packages">
        Grant search and step-by-step roadmaps are free. Unlock Area 01, Area 02, or both.
      </PageHeader>
      <RefundCard />
      <Subscriptions />
      <PricingCards />
      <div className="mt-8 bg-white border border-slate-200 border-l-4 border-l-amber-500 rounded-lg p-6 flex gap-4 items-start">
        <BadgeCheck className="h-6 w-6 text-amber-600 shrink-0" />
        <p className="text-sm text-slate-600">
          <b className="text-slate-900">Certified human final review</b> is billed per proposal: ${REVIEW_PRICE} flat, or ${REVIEW_MEMBER_PRICE} on the Full Verify Suite. Start it from any proposal page.
        </p>
      </div>
    </div>
  );
}
